import { FormInstance, Input, Select } from "antd";
import { useAtomValue } from "jotai";
import { Ref } from "react";
import {
  MdCategory,
  MdFormatQuote,
  MdInfoOutline,
  MdLanguage,
  MdLocalMovies,
  MdTitle,
  MdTranslate,
} from "react-icons/md";
import { Subject } from "rxjs";
import { masterAtom } from "../../../../atoms/masters.atom";
import { masterToOptions } from "../../../../utils/functions/misc/masterToOptions";
import {
  Field,
  FieldGrid,
  Section,
} from "../../components/section-card.component";
import { CustomUrlField } from "../../components/custom-url.component";

export const StepOne = ({
  visible = true,
  onFilmTitleChange,
  titleFieldsAutoFocus,
  fillHeight,
  form,
  customUrlInputRef,
  customUrlSubject$,
  customUrl,
  urlVerified,
  showVerification,
}: {
  visible?: boolean;
  onFilmTitleChange?: (title: string) => void;
  titleFieldsAutoFocus?: boolean;
  fillHeight?: boolean;
  form: FormInstance;
  customUrlInputRef: Ref<HTMLInputElement>;
  customUrlSubject$: Subject<string>;
  customUrl: string;
  urlVerified: boolean | null;
  showVerification: boolean;
}) => {
  const master = useAtomValue(masterAtom);

  return (
    <div
      style={{
        display: visible ? "flex" : "none",
        width: "100%",
        height: "100%",
        minHeight: 0,
        flexDirection: "column",
      }}
    >
      <Section
        icon={<MdInfoOutline />}
        title="Basic Details"
        subtitle="Tell us about your film — its language, category, genre and a short synopsis."
        fillHeight={fillHeight}
      >
        <FieldGrid>
          <CustomUrlField
            inputRef={customUrlInputRef}
            subject$={customUrlSubject$}
            form={form}
            customUrl={customUrl}
            urlVerified={urlVerified}
            showVerification={showVerification}
          />
          <Field
            name="title"
            label="Original Title"
            icon={<MdTitle />}
            required
            rules={[{ required: true, message: "Please enter film title!" }]}
            helper="The title as it appears on the film."
          >
            <Input
              size="large"
              placeholder="Enter film title"
              autoFocus={titleFieldsAutoFocus}
              onChange={(e) => onFilmTitleChange?.(e.target.value)}
            />
          </Field>
          <Field
            name="english_title"
            label="English Title"
            icon={<MdTranslate />}
            required
            rules={[
              { required: true, message: "Please enter English title!" },
            ]}
            helper="Use the same as the original title if the film is in English."
          >
            <Input size="large" placeholder="Enter English title" />
          </Field>
          <Field
            name="language"
            label="Original Language"
            icon={<MdLanguage />}
            required
            rules={[{ required: true }]}
            helper="The language the film was originally produced in."
          >
            <Select
              size="large"
              options={masterToOptions("languages", master, [], true)}
              placeholder="Select original language"
              showSearch
              allowClear
            />
          </Field>
          <Field
            name="category"
            label="Category"
            icon={<MdCategory />}
            required
            rules={[{ required: true }]}
            helper="What kind of film is this — short, feature, documentary…"
          >
            <Select
              size="large"
              showSearch
              options={masterToOptions("film_category", master)}
              placeholder="Select category"
            />
          </Field>
          <Field
            name="tagline"
            label="Tagline"
            icon={<MdFormatQuote />}
            required
            rules={[{ required: true }]}
            helper="A one-line hook for your audience."
          >
            <Input size="large" placeholder="A short, memorable tagline…" />
          </Field>
          <Field
            name="genre"
            label="Genre"
            icon={<MdLocalMovies />}
            required
            rules={[{ required: true }]}
            helper="Choose all genres that apply."
          >
            <Select
              size="large"
              showSearch
              mode="multiple"
              allowClear
              options={masterToOptions("genre", master)}
              placeholder="Select genres"
            />
          </Field>
        </FieldGrid>
      </Section>
    </div>
  );
};
